/**
 * Le pilotage, adossé à Supabase.
 *
 * Ce dépôt ne lit **aucun chantier lui-même** : il passe par le dépôt
 * chantiers, celui-là même que sert l'écran Chantiers. C'est ce qui garantit
 * que la marge affichée sur le tableau de bord est la marge affichée sur la
 * fiche — au centime, et sans qu'on ait à s'en occuper. Recopier ici la
 * requête et son calcul de santé aurait produit deux vérités pour un même
 * chantier, et c'est toujours celle que personne ne relit qui devient fausse.
 *
 * Il n'ajoute donc que ce qui manquait : les heures pointées et la marge
 * annoncée au devis.
 */
import type {
  Backlog,
  ChantierProfitability,
  EmployeeHours,
  ExecutionContext,
  PilotageRepository,
  ProjectLedger,
  ProjectRepository,
  TeamRepository,
} from "@dker/core";
import {
  DomainError,
  OPEN_PROJECT_STATUSES,
  clientDisplayName,
  computeEmployeeProfitability,
  isProjectCost,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown): number => (v == null ? 0 : Number(v));

/** Les chantiers sur lesquels du travail est encore attendu. */
const OUVERTS = OPEN_PROJECT_STATUSES;

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabasePilotageRepository(
  clientFor: DbClientFactory,
  projects: ProjectRepository,
  team: TeamRepository,
): PilotageRepository {
  return {
    async chantierProfitability(ctx: ExecutionContext) {
      const ouverts = await projects.list(ctx, { status: [...OUVERTS] });
      if (ouverts.length === 0) return [];

      const ids = ouverts.map((p) => p.id);

      // Le lien vers le devis d'origine n'est pas dans `ProjectSummary` : il
      // n'a d'usage que pour cette comparaison, et l'ajouter au résumé le
      // ferait voyager dans tous les écrans qui n'en ont que faire.
      const [{ data: liens, error: eLiens }, { data: heures, error: eHeures }] =
        await Promise.all([
          clientFor(ctx).from("projects").select("id, quote_id").in("id", ids),
          clientFor(ctx).from("time_entries").select("project_id, hours, kind").in("project_id", ids),
        ]);
      if (eLiens) fail(eLiens);
      if (eHeures) fail(eHeures);

      const quoteParProjet = new Map<string, string>();
      for (const l of (liens ?? []) as Row[]) {
        if (l.quote_id) quoteParProjet.set(l.id, l.quote_id);
      }

      const quoteIds = [...new Set(quoteParProjet.values())];
      let margeParDevis = new Map<string, number>();
      if (quoteIds.length > 0) {
        const { data: devis, error } = await clientFor(ctx)
          .from("quotes")
          .select("id, margin_percent")
          .in("id", quoteIds);
        if (error) fail(error);
        margeParDevis = new Map(
          ((devis ?? []) as Row[])
            .filter((d) => d.margin_percent != null)
            .map((d) => [d.id as string, num(d.margin_percent)]),
        );
      }

      // Seules les heures imputables au coût d'un chantier. La liste vit dans
      // `isProjectCost`, partagée avec le pointage : le trajet et la formation
      // sont du temps de travail sans être du temps passé sur l'ouvrage.
      const heuresParProjet = new Map<string, number>();
      for (const e of (heures ?? []) as Row[]) {
        if (!e.project_id || !isProjectCost(e.kind)) continue;
        heuresParProjet.set(e.project_id, (heuresParProjet.get(e.project_id) ?? 0) + num(e.hours));
      }

      return ouverts.map<ChantierProfitability>((p) => {
        const quoteId = quoteParProjet.get(p.id);
        return {
          projectId: p.id,
          projectName: p.name,
          clientName: p.client ? clientDisplayName(p.client) : null,
          progressPercent: p.health.progressPercent,
          hoursWorked: Math.round((heuresParProjet.get(p.id) ?? 0) * 100) / 100,
          quotedMarginPercent: quoteId ? (margeParDevis.get(quoteId) ?? null) : null,
          realMarginPercent: p.health.marginPercent,
        };
      });
    },

    async backlog(ctx: ExecutionContext) {
      const [{ data: acceptes, error: eDevis }, { data: rattaches, error: eProjets }] =
        await Promise.all([
          clientFor(ctx)
            .from("quotes")
            .select("id, total_ht, estimated_duration_days")
            .eq("status", "accepted"),
          clientFor(ctx).from("projects").select("quote_id, status").not("quote_id", "is", null),
        ]);
      if (eDevis) fail(eDevis);
      if (eProjets) fail(eProjets);

      // « Démarré » se juge sur le statut du chantier et non sur son existence :
      // accepter un devis crée le chantier, encore *planifié*. Le compter comme
      // démarré viderait le carnet à l'instant même où il se remplit.
      const demarres = new Set(
        ((rattaches ?? []) as Row[])
          .filter((p) => p.status !== "planned")
          .map((p) => p.quote_id as string),
      );

      const restants = ((acceptes ?? []) as Row[]).filter((q) => !demarres.has(q.id));
      const avecDuree = restants.filter((q) => q.estimated_duration_days != null);
      const jours = avecDuree.reduce((s, q) => s + num(q.estimated_duration_days), 0);

      return {
        signedHt: Math.round(restants.reduce((s, q) => s + num(q.total_ht), 0) * 100) / 100,
        quoteCount: restants.length,
        // `null` plutôt que zéro quand aucun devis ne porte de durée : afficher
        // « 0 semaine » de carnet sur 96 000 € signés serait un mensonge, et
        // c'est le genre de chiffre juste-en-apparence que ce dépôt évite.
        weeks: avecDuree.length > 0 ? Math.round((jours / 5) * 10) / 10 : null,
      } satisfies Backlog;
    },

    async employeeProfitability(ctx: ExecutionContext) {
      // Tous les chantiers, et non les seuls ouverts : un compagnon qui a
      // passé l'automne sur un chantier soldé en décembre ne doit pas voir sa
      // contribution disparaître le jour où le chantier se ferme. C'est la
      // seule lecture de ce dépôt qui regarde au-delà des chantiers en cours,
      // et c'est ce qui la rend cumulative — le tableau le dit.
      const [salaries, tous] = await Promise.all([
        // Inactifs compris : un salarié parti en juin a coûté et rapporté, et
        // le retirer du tableau ferait disparaître sa part de la marge sans
        // que rien ne l'explique.
        team.listEmployees(ctx, true),
        projects.list(ctx, {}),
      ]);
      if (salaries.length === 0) return [];

      const ids = tous.map((p) => p.id);
      if (ids.length === 0) {
        return computeEmployeeProfitability(salaries, [], []);
      }

      // `labour_cost_ht` n'est pas dans `ProjectSummary` : il n'a d'usage que
      // pour cette répartition, et l'ajouter au résumé le ferait voyager dans
      // tous les écrans qui n'en ont que faire. Même arbitrage que `quote_id`
      // au-dessus.
      const [{ data: mo, error: eMo }, { data: heures, error: eHeures }] = await Promise.all([
        clientFor(ctx).from("projects").select("id, labour_cost_ht").in("id", ids),
        clientFor(ctx)
          .from("time_entries")
          .select("employee_id, project_id, hours, kind, hourly_cost, overtime_premium"),
      ]);
      if (eMo) fail(eMo);
      if (eHeures) fail(eHeures);

      const moParProjet = new Map(
        ((mo ?? []) as Row[]).map((p) => [p.id as string, num(p.labour_cost_ht)]),
      );

      const ledger = tous.map<ProjectLedger>((p) => ({
        projectId: p.id,
        // Le facturé vient de `computeInvoicedHt`, via la santé du chantier :
        // c'est lui qui règle le piège de l'acompte suivi d'une facture finale,
        // et le recopier ici le rejouerait de travers un jour.
        invoicedHt: p.health.invoicedHt,
        spentHt: p.spentHt,
        labourCostHt: moParProjet.get(p.id) ?? 0,
      }));

      const pointages = ((heures ?? []) as Row[]).map<EmployeeHours>((e) => ({
        employeeId: e.employee_id as string,
        projectId: (e.project_id as string | null) ?? null,
        hours: num(e.hours),
        charged: isProjectCost(e.kind),
        // `null` et non zéro : la policy `patron_only` peut avoir retiré le
        // taux, et une heure gratuite rendrait la contribution flatteuse. Le
        // calcul compte ces heures à part et l'écran les annonce.
        hourlyCost: e.hourly_cost == null ? null : num(e.hourly_cost),
        overtimePremium: num(e.overtime_premium),
      }));

      return computeEmployeeProfitability(salaries, pointages, ledger);
    },
  };
}
